import { useState } from "react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@shared/components/ui/dialog";
import { Button } from "@shared/components/ui/button";
import { Label } from "@shared/components/ui/label";
import { Input } from "@shared/components/ui/input";
import { Badge } from "@shared/components/ui/badge";
import { Truck, User, DollarSign } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import * as pickupService from "@features/pickups/services/pickupService";
import * as packagesService from "@features/packages/services/packagesService";
import { SelectUser } from "@/features/users/SelectUser";

const AssignCollectorToPickupDialog = ({ pickupData, trigger, onSuccess }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [selectedCollector, setSelectedCollector] = useState("");
  const [selectedCollectorData, setSelectedCollectorData] = useState(null);
  const [pickupPrice, setPickupPrice] = useState(
    pickupData?.collectorPickupPrice || ""
  );

  // Get available collectors
  const { data: collectors } = useQuery({
    queryKey: ["collectors"],
    queryFn: () => packagesService.getUsersByRole("collector"),
    enabled: open,
  });

  // Get default price when collector is selected
  const getDefaultPrice = () => {
    if (selectedCollectorData?.collectorPricing?.defaultPickupPrice) {
      return selectedCollectorData.collectorPricing.defaultPickupPrice;
    }
    return 10; // Fallback default
  };

  const assignCollectorMutation = useMutation({
    mutationFn: (data) =>
      pickupService.assignCollectorToPickup(pickupData._id, data),
    onSuccess: () => {
      // Invalidate pickup-related queries
      queryClient.invalidateQueries({ queryKey: ["pickups"] });
      queryClient.invalidateQueries({ queryKey: ["pickup"] });
      queryClient.invalidateQueries({ queryKey: ["pickup-stats"] });
      queryClient.invalidateQueries({ queryKey: ["pickup-analytics"] });
      queryClient.invalidateQueries({ queryKey: ["recent-pickups"] });

      // Invalidate package-related queries
      queryClient.invalidateQueries({ queryKey: ["packages"] });
      queryClient.invalidateQueries({ queryKey: ["collector-packages"] });
      queryClient.invalidateQueries({ queryKey: ["collector-pickups-widget"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-stats"] });

      setOpen(false);
      setSelectedCollector("");
      toast.success(t("pickup.collectorAssignedToPickupSuccessfully"));

      // Call optional success callback
      if (onSuccess) {
        onSuccess();
      }
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message ||
          t("pickup.failedToAssignCollectorToPickup")
      );
    },
  });

  const unassignCollectorMutation = useMutation({
    mutationFn: () => pickupService.unassignCollectorFromPickup(pickupData._id),
    onSuccess: () => {
      // Invalidate pickup-related queries
      queryClient.invalidateQueries({ queryKey: ["pickups"] });
      queryClient.invalidateQueries({ queryKey: ["pickup"] });
      queryClient.invalidateQueries({ queryKey: ["pickup-stats"] });
      queryClient.invalidateQueries({ queryKey: ["pickup-analytics"] });
      queryClient.invalidateQueries({ queryKey: ["recent-pickups"] });

      // Invalidate package-related queries
      queryClient.invalidateQueries({ queryKey: ["packages"] });
      queryClient.invalidateQueries({ queryKey: ["collector-packages"] });
      queryClient.invalidateQueries({ queryKey: ["collector-pickups-widget"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-stats"] });

      setOpen(false);
      toast.success(t("pickup.collectorUnassignedFromPickupSuccessfully"));

      // Call optional success callback
      if (onSuccess) {
        onSuccess();
      }
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message ||
          t("pickup.failedToUnassignCollectorFromPickup")
      );
    },
  });

  const handleAssignCollector = () => {
    if (!selectedCollector) {
      toast.error(t("pickup.pleaseSelectACollector"));
      return;
    }

    // Use entered price or default
    const finalPrice =
      pickupPrice && pickupPrice > 0 ? Number(pickupPrice) : getDefaultPrice();

    assignCollectorMutation.mutate({
      collectorId: selectedCollector,
      collectorPickupPrice: finalPrice,
    });
  };

  // Update price when collector changes
  const handleCollectorSelect = (collectorId) => {
    setSelectedCollector(collectorId);
    const collector = collectors?.find((c) => c._id === collectorId);
    setSelectedCollectorData(collector);

    // Set default price if no custom price entered
    if (!pickupPrice) {
      const defaultPrice = collector?.collectorPricing?.defaultPickupPrice || 10;
      setPickupPrice(defaultPrice);
    }
  };

  const handleUnassignCollector = () => {
    unassignCollectorMutation.mutate();
  };

  const isAssigned = pickupData.collector;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck className="h-5 w-5" />
            {t("pickup.managePickupAssignment")}
          </DialogTitle>
          <DialogDescription>
            {t("pickup.assignOrUnassignCollector")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Pickup Info */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">
              {t("pickup.pickupDetails")}
            </Label>
            <div className="p-3 bg-muted rounded-lg space-y-2">
              <div className="flex justify-between text-sm">
                <span>{t("pickup.pickupId")}:</span>
                <span className="font-medium">{pickupData._id}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>{t("pickup.type")}:</span>
                <span className="font-medium capitalize">
                  {pickupData.type}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span>{t("common.status")}:</span>
                <Badge variant="outline" className="text-xs">
                  {pickupData.status?.replace(/_/g, " ").toUpperCase()}
                </Badge>
              </div>
              {pickupData.type === "package" && (
                <div className="flex justify-between text-sm">
                  <span>{t("pickup.packagesCount")}:</span>
                  <span className="font-medium">
                    {pickupData.packages?.length || 0}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Current Assignment */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">
              {t("pickup.currentAssignment")}
            </Label>
            <div className="p-3 bg-muted rounded-lg">
              {isAssigned ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4" />
                    <span className="font-medium">
                      {pickupData.collector?.fullName}
                    </span>
                  </div>
                  <Badge variant="default">{t("pickup.assigned")}</Badge>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <User className="h-4 w-4" />
                  <span>{t("pickup.noCollectorAssigned")}</span>
                </div>
              )}
              {isAssigned && pickupData.collectorPickupPrice > 0 && (
                <div className="mt-2 pt-2 border-t">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">
                      {t("pickup.pickupPrice")}:
                    </span>
                    <span className="font-semibold text-primary">
                      {pickupData.collectorPickupPrice} DH
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Collector Selection */}
          {!isAssigned && (
            <>
              <div className="space-y-2">
                <Label htmlFor="collector" className="text-sm font-medium">
                  {t("pickup.selectCollector")} *
                </Label>
                <SelectUser
                  role="collector"
                  user={selectedCollector}
                  onSelectUser={handleCollectorSelect}
                  placeholder={t("pickup.selectACollector")}
                  searchPlaceholder={t("pickup.searchCollectors")}
                  className="w-full"
                />
              </div>

              {selectedCollector && (
                <div className="p-2 bg-blue-50 dark:bg-blue-950/20 rounded-md border border-blue-200 dark:border-blue-800">
                  <p className="text-xs text-blue-700 dark:text-blue-300">
                    <span className="font-medium">
                      {t("pickup.defaultPickupPrice")}:
                    </span>{" "}
                    {getDefaultPrice()} DH
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label
                  htmlFor="pickupPrice"
                  className="flex items-center gap-2"
                >
                  <DollarSign className="h-4 w-4" />
                  {t("pickup.pickupPrice")} (DH)
                </Label>
                <Input
                  id="pickupPrice"
                  type="number"
                  min="0"
                  step="0.01"
                  value={pickupPrice}
                  onChange={(e) => setPickupPrice(e.target.value)}
                  placeholder={`${t("pickup.default")}: ${
                    selectedCollector ? getDefaultPrice() : 10
                  } DH`}
                  className="w-full"
                />
                <p className="text-xs text-muted-foreground">
                  {t(
                    "pickup.thisIsTheAmountTheCollectorWillEarnForCompletingThisPickup"
                  )}
                  {!pickupPrice && selectedCollector && (
                    <span className="block mt-1 text-blue-600 dark:text-blue-400">
                      {t("pickup.leaveEmptyToUseDefault")} ({getDefaultPrice()}{" "}
                      DH)
                    </span>
                  )}
                </p>
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            {t("common.cancel")}
          </Button>
          {isAssigned ? (
            <Button
              variant="destructive"
              onClick={handleUnassignCollector}
              disabled={unassignCollectorMutation.isPending}
            >
              {unassignCollectorMutation.isPending
                ? t("pickup.unassigning")
                : t("pickup.unassignCollector")}
            </Button>
          ) : (
            <Button
              onClick={handleAssignCollector}
              disabled={assignCollectorMutation.isPending || !selectedCollector}
            >
              {assignCollectorMutation.isPending
                ? t("pickup.assigning")
                : t("pickup.assignCollector")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AssignCollectorToPickupDialog;
